// CompanyDocuments.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Card,
  CardContent,
  Stack,
} from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { TAB_LIGHT_BLUE } from "../CompanyTabHeaderShared";
import TabSectionHeader from "../../TabSectionHeader";
import {
  pillButtonFilledSx,
  pillButtonInteractiveSx,
  withPillButtonClassName,
} from "../../../utils/pillButtonStyles";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

const getToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("authToken") ||
  localStorage.getItem("jwt") ||
  localStorage.getItem("accessToken") ||
  "";

const formatFileSize = (size) => {
  const num = Number(size);
  if (!Number.isFinite(num) || num <= 0) return "PDF";
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${Math.round(num / 1024)} KB`;
  return `${(num / (1024 * 1024)).toFixed(1)} MB`;
};

const parseDocuments = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const resolveDocumentUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("/")) return `${API_BASE}${raw}`;
  return `${API_BASE}/${raw}`;
};

const normalizeDocumentName = (doc = {}, index = 0) => {
  const candidates = [doc.original_name, doc.filename, doc.name, doc.title]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  const fileLikeName = candidates.find((value) => /\.pdf$/i.test(value));
  if (fileLikeName) return fileLikeName;

  const safeLabel = candidates.find((value) => value.length <= 120 && !/[\r\n]/.test(value));
  return safeLabel || `Document ${index + 1}.pdf`;
};

const normalizeDocument = (doc = {}, index = 0) => ({
  id: doc.id || doc.filename || doc.url || `${doc.name || "document"}-${index}`,
  name: normalizeDocumentName(doc, index),
  size: formatFileSize(doc.size),
  status: "Uploaded",
  url: resolveDocumentUrl(doc.url),
  filename: String(doc.filename || "").trim(),
  mime_type: String(doc.mime_type || doc.mimeType || "").trim(),
});

const CompanyDocuments = ({
  company = {},
  companyId = null,
  entityType = "lead",
  onCompanyChange,
}) => {
  const fileInputRef = useRef(null);
  const onCompanyChangeRef = useRef(onCompanyChange);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [previewDocument, setPreviewDocument] = useState(null);
  const normalizedEntityType = String(entityType || company?.entityType || "lead").toLowerCase();
  const documentsOwnerType = normalizedEntityType === "company" ? "company" : "lead";
  const canUploadDocuments = Boolean(companyId) && ["company", "lead"].includes(documentsOwnerType);

  useEffect(() => {
    onCompanyChangeRef.current = onCompanyChange;
  }, [onCompanyChange]);

  const normalizedInitialDocuments = useMemo(
    () => parseDocuments(company.documents_json || company.documentsJson || company.documents).map(normalizeDocument),
    [company.documents_json, company.documentsJson, company.documents]
  );

  useEffect(() => {
    setDocuments(normalizedInitialDocuments);
  }, [normalizedInitialDocuments]);

  const applyDocuments = useCallback(
    (rawDocuments) => {
      const nextRawDocuments = Array.isArray(rawDocuments) ? rawDocuments : [];
      setDocuments(nextRawDocuments.map(normalizeDocument));
      onCompanyChangeRef.current?.({
        id: companyId,
        documents_json: nextRawDocuments,
        documentsJson: nextRawDocuments,
      });
    },
    [companyId]
  );

  const fetchDocuments = useCallback(async () => {
    if (!canUploadDocuments || !companyId) return;

    const token = getToken();
    setLoading(true);
    setError("");
    try {
      const basePath =
        documentsOwnerType === "company"
          ? `${API_BASE}/api/companies/${encodeURIComponent(companyId)}/documents`
          : `${API_BASE}/api/leads/${encodeURIComponent(companyId)}/documents`;
      const res = await fetch(basePath, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(token ? { "x-access-token": token } : {}),
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Failed to load documents (${res.status})`);
      applyDocuments(data?.documents || []);
    } catch (err) {
      setError(err?.message || "Failed to load company documents.");
    } finally {
      setLoading(false);
    }
  }, [applyDocuments, canUploadDocuments, companyId, documentsOwnerType]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handlePickDocument = useCallback(() => {
    if (!canUploadDocuments || uploading) return;
    fileInputRef.current?.click();
  }, [canUploadDocuments, uploading]);

  const handleDocumentClick = useCallback((doc) => {
    if (!doc?.url) return;
    setPreviewDocument(doc);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewDocument(null);
  }, []);

  const handleFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;
      const fileType = String(file.type || "").toLowerCase();
      const fileName = String(file.name || "").toLowerCase();
      const isPdf = fileType === "application/pdf" || fileName.endsWith(".pdf");
      if (!isPdf) {
        setError("Only PDF files are allowed.");
        return;
      }

      const token = getToken();
      const formData = new FormData();
      formData.append("document", file);

      setUploading(true);
      setError("");
      try {
        const basePath =
          documentsOwnerType === "company"
            ? `${API_BASE}/api/companies/${encodeURIComponent(companyId)}/documents`
            : `${API_BASE}/api/leads/${encodeURIComponent(companyId)}/documents`;
        const res = await fetch(basePath, {
          method: "POST",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(token ? { "x-access-token": token } : {}),
          },
          body: formData,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || `Failed to upload document (${res.status})`);
        applyDocuments(data?.documents || []);
      } catch (err) {
        setError(err?.message || "Failed to upload company document.");
      } finally {
        setUploading(false);
      }
    },
    [applyDocuments, companyId, documentsOwnerType]
  );

  return (
    <Card sx={{ mt: 3, mb: 4, borderRadius: 0, boxShadow: "none" }}>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <TabSectionHeader
        title="Documents"
        dividerColor={TAB_LIGHT_BLUE}
        actionNode={
          <Button
            size="small"
            variant="contained"
            className={withPillButtonClassName()}
            startIcon={
              uploading ? <CircularProgress size={14} color="inherit" /> : <AddRoundedIcon sx={{ fontSize: 16 }} />
            }
            sx={[
              pillButtonInteractiveSx,
              pillButtonFilledSx,
              {
                minWidth: 170,
                fontWeight: 400,
                color: "#fff",
                boxShadow: "none",
                "&.Mui-disabled": {
                  color: "#fff",
                  opacity: 0.5,
                },
              },
            ]}
            onClick={handlePickDocument}
            disabled={!canUploadDocuments || uploading}
          >
            {uploading ? "Uploading..." : "Add New Document"}
          </Button>
        }
      />

      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        {error ? (
          <Typography sx={{ color: "#DC2626", fontSize: 12, fontWeight: 600, mb: 1.5 }}>
            {error}
          </Typography>
        ) : null}

        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, color: "#6B7280", py: 1 }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontSize: 13 }}>Loading documents...</Typography>
          </Box>
        ) : null}

        {!loading && !documents.length && !error ? (
          <Box
            sx={{
              border: "1px dashed #D7E0EA",
              borderRadius: 2.5,
              backgroundColor: "#F8FAFC",
              px: { xs: 2, md: 2.5 },
              py: { xs: 2.5, md: 3 },
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.75,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#344054" }}>
                No documents yet
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: 12.5, color: "#667085", lineHeight: 1.55 }}>
                Upload PDF files here to keep company reference material aligned with the other detail sections.
              </Typography>
            </Box>

            {canUploadDocuments ? (
              <Button
                size="small"
                variant="contained"
                className={withPillButtonClassName()}
                startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                sx={[
                  pillButtonInteractiveSx,
                  pillButtonFilledSx,
                  {
                    fontWeight: 400,
                    px: 1.8,
                    boxShadow: "none",
                  },
                ]}
                onClick={handlePickDocument}
                disabled={uploading}
              >
                Add New Document
              </Button>
            ) : null}
          </Box>
        ) : null}

        {!!documents.length ? (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, minmax(0, 1fr))",
              },
            }}
          >
            {documents.map((doc) => (
              <Box
                key={doc.id}
                onClick={() => handleDocumentClick(doc)}
                sx={{
                  borderRadius: 2.5,
                  border: "1px solid #D7E0EA",
                  backgroundColor: "#FFFFFF",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1.5,
                  px: 2,
                  py: 1.75,
                  cursor: doc.url ? "pointer" : "default",
                  transition: "border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease",
                  "&:hover": doc.url
                    ? {
                        borderColor: "#B8CCFF",
                        boxShadow: "0 12px 24px rgba(52,81,244,0.08)",
                        transform: "translateY(-1px)",
                      }
                    : undefined,
                }}
              >
                <Box display="flex" alignItems="flex-start" gap={1.5} sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      backgroundColor: "#F5F8FF",
                      border: "1px solid #D9E5F3",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: "0 0 auto",
                    }}
                  >
                    <InsertDriveFileOutlinedIcon sx={{ fontSize: 20, color: "#3451F4" }} />
                  </Box>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: "#111827", whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {doc.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#667085", display: "block", mt: 0.45, whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {doc.size}
                    </Typography>
                  </Box>
                </Box>

                <Stack alignItems="flex-end" spacing={1} sx={{ flex: "0 0 auto", pl: 1 }}>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.45,
                      borderRadius: 999,
                      backgroundColor: "#ECFDF3",
                      color: "#027A48",
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {doc.status}
                  </Box>
                  {doc.url ? (
                    <OpenInNewRoundedIcon sx={{ fontSize: 18, color: "#9C4FFF" }} />
                  ) : (
                    <CheckCircleOutlineIcon sx={{ color: "#9C4FFF", fontSize: 18 }} />
                  )}
                </Stack>
              </Box>
            ))}
          </Box>
        ) : null}
      </CardContent>

      <Dialog
        open={Boolean(previewDocument)}
        onClose={handleClosePreview}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: "calc(100% - 24px)", md: "min(1100px, calc(100% - 80px))" },
            maxWidth: "1100px",
            height: { xs: "calc(100% - 24px)", md: "calc(100% - 64px)" },
            maxHeight: "960px",
            borderRadius: 3,
            overflow: "hidden",
            background: "#fff",
          },
        }}
      >
        <Box
          sx={{
            px: 2.25,
            py: 1.5,
            borderBottom: "1px solid #EAECF0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography data-popup-section sx={{ fontWeight: 700, color: "#3451F4", fontSize: 15 }}>Document</Typography>
            <Typography sx={{ fontSize: 12, color: "#667085" }} noWrap>
              {previewDocument?.name || "PDF preview"}
            </Typography>
          </Box>

          <Box data-popup-header sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Button
              component="a"
              href={previewDocument?.url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: "none", color: "#9C4FFF", fontWeight: 500 }}
            >
              Open in new tab
            </Button>

            <IconButton data-popup-close onClick={handleClosePreview} size="small">
              <CloseRoundedIcon sx={{ fontSize: 18, color: "#475467" }} />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            height: "100%",
            overflow: "auto",
            background: "#fff",
            p: { xs: 1.5, md: 2 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              mx: "auto",
              background: "#fff",
              overflow: "hidden",
              borderTop: "7px solid #F5C542",
              borderRadius: 1,
            }}
          >
            <iframe
              title={previewDocument?.name || "PDF preview"}
              src={
                previewDocument?.url
                  ? `${previewDocument.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`
                  : "about:blank"
              }
              style={{
                width: "100%",
                height: "calc(100vh - 180px)",
                minHeight: "620px",
                border: "0",
                outline: "none",
                display: "block",
                background: "#fff",
              }}
            />
          </Box>
        </Box>
      </Dialog>
    </Card>
  );
};

export default CompanyDocuments;
